/**
 * Mặt nhân vật + cờ "dựng từ mặt cũ" + ô *Gen lại ▾* — logic THUẦN phía FE (đợt 260922 E3-P5). Tách khỏi file import antd để jest chạy được.
 *
 * Luật nguồn mặt nằm ở backend (`admin-service/src/utils/filmCharacterSheet.ts` — `pickSheetFace`); cờ cũ và loại ảnh vào do SERVER tính
 * (`stale`, `sheetInput` trên từng look). Ở đây chỉ dựng chữ hiển thị + thứ tự gen lại + lọc model.
 */
import type { FaceSource, ImageModelInfo, SheetInputKind } from '../../types/film';

interface CharLike {
  faceSource?: FaceSource | string | null;
  faceRefUrl?: string | null;
  seedanceCharacterId?: string | null;
  seedancePreviewUrl?: string | null;
  canonicalRefUrl?: string | null;
}

const norm = (v: unknown): FaceSource | null => {
  const t = String(v ?? '').trim().toUpperCase();
  return t === 'DC' || t === 'UPLOAD' || t === 'TEXT' || t === 'AI_FACE' ? t : null;
};

export interface FaceChip { text: string; title: string; tone: 'ok' | 'warn' | '' }

/** Chip *Mặt* trên thẻ: nguồn mặt ảnh bộ đồ SẼ dùng. */
export function faceSourceChip(c: CharLike): FaceChip {
  const fs = norm(c.faceSource);
  if (fs === 'UPLOAD' && c.faceRefUrl) return { text: 'Mặt: Ảnh tải lên', title: 'Ảnh bộ chính dựng từ ảnh mặt bạn tải lên.', tone: 'ok' };
  if (fs === 'DC' && c.seedancePreviewUrl) return { text: 'Mặt: Avatar', title: 'Ảnh bộ chính dựng từ avatar (DC) đang gán.', tone: 'ok' };
  if (fs === 'AI_FACE' && c.faceRefUrl) return { text: 'Mặt AI', title: 'Ảnh bộ chính dựng từ mặt AI bạn đã chọn trong bộ tạo mặt.', tone: 'ok' };
  if (fs === 'TEXT') return { text: 'Mặt: Từ mô tả', title: 'Ảnh bộ chính gen từ 7 nhãn ngoại hình, không ảnh mặt.', tone: 'ok' };
  if (fs) return { text: 'Mặt: thiếu ảnh', title: 'Nguồn đã chọn không còn ảnh — ảnh bộ đồ đang dựng theo thứ tự tự động.', tone: 'warn' };
  const auto = c.seedancePreviewUrl ? 'Avatar' : c.canonicalRefUrl ? 'Ảnh canonical' : 'Từ mô tả';
  return { text: `Mặt: tự động · ${auto}`, title: 'Chưa chọn nguồn mặt — dùng thứ tự cũ: avatar → ảnh canonical → từ mô tả. Bấm ☺ Chọn mặt để chọn.', tone: '' };
}

const LABEL: Record<FaceSource, string> = { DC: 'avatar', UPLOAD: 'ảnh mặt tải lên', TEXT: 'mô tả (không ảnh mặt)', AI_FACE: 'mặt AI' };

/** Dòng nhắc dưới chip *Mặt*. Rỗng = không có gì phải nói. */
export function faceNotes(c: CharLike): string[] {
  const fs = norm(c.faceSource);
  const out: string[] = [];
  // Các đường khác (clip BytePlus, giọng V.O., Xưởng ảnh cảnh, khung chủ scene) vẫn đọc avatar làm mặt tới Epic 5 — phản biện 24/09.
  if ((fs === 'UPLOAD' || fs === 'TEXT' || fs === 'AI_FACE') && c.seedanceCharacterId) {
    out.push(`Ảnh bộ đồ dùng ${LABEL[fs]} — clip BytePlus vẫn dùng avatar đang gán (tới Epic 5).`);
  }
  if (fs === 'DC' && !c.seedancePreviewUrl) out.push('Avatar chưa có ảnh — ảnh bộ đồ không dựng từ avatar.');
  if (fs === 'UPLOAD' && !c.faceRefUrl) out.push('Ảnh mặt tải lên đã mất — ảnh bộ đồ dựng theo thứ tự tự động.');
  if (fs === 'AI_FACE' && !c.faceRefUrl) out.push('Ảnh mặt AI đã mất — ảnh bộ đồ dựng theo thứ tự tự động.');
  return out;
}

/** Ảnh nhỏ góc thẻ = mặt ĐANG dùng cho ảnh bộ đồ. */
export function faceThumbOf(c: CharLike): string | null {
  if ((norm(c.faceSource) === 'UPLOAD' || norm(c.faceSource) === 'AI_FACE') && c.faceRefUrl) return c.faceRefUrl;
  if (norm(c.faceSource) === 'TEXT') return null;
  return c.seedancePreviewUrl || c.canonicalRefUrl || null;
}

/** Khối "Ảnh mặt đã tải / Dùng lại ảnh này" của tab tải lên: CHỈ khi nguồn là UPLOAD — `face_ref_url` dùng chung với mặt AI, không coi ảnh AI là ảnh tải lên (260930 P2). */
export function showUploadedFaceBlock(c: CharLike): boolean {
  return norm(c.faceSource) === 'UPLOAD' && !!c.faceRefUrl;
}

/** Nhãn model thật sinh ra ứng viên: Flare, hoặc NB2 khi Flare từ chối (server ghi model THẬT vào `film_image_variants.model`). */
export function faceCandidateModelLabel(model?: string | null): string {
  const m = String(model ?? '').trim();
  if (!m) return '—';
  if (/flare/i.test(m)) return 'Flare';
  if (/gemini-3\.1-flash-image/i.test(m)) return 'NB2 (Flare từ chối)';
  return m;
}

/** Ứng viên này đang là mặt dùng: nguồn AI_FACE và cùng ảnh. */
export function isCandidateInUse(c: CharLike, v: { url: string }): boolean {
  return norm(c.faceSource) === 'AI_FACE' && !!c.faceRefUrl && c.faceRefUrl === v.url;
}

/** Prompt điền sẵn vào ô Prompt: của ứng viên MỚI NHẤT (`candidates` đã xếp mới nhất trước — server trả vậy); không có ⇒ rỗng (= dùng prompt mặc định). */
export function defaultFacePrompt(candidates?: Array<{ prompt?: string | null }> | null): string {
  return String(candidates?.[0]?.prompt ?? '').trim();
}

/** Có gửi `promptOverride` khi bấm *Tạo mặt* không: ô có chữ ⇒ LUÔN gửi đúng nội dung ô (thấy gì gửi nấy); ô trống ⇒ không (server dùng prompt mặc định). */
export function shouldSendPromptOverride(text: string): boolean {
  return !!String(text ?? '').trim();
}

/** Nhóm tuổi của bộ lọc thư viện avatar (catalog tuổi 18–100). */
export const AGE_BUCKETS: Array<{ key: string; label: string; min?: number; max?: number }> = [
  { key: 'all', label: 'Mọi tuổi' },
  { key: 'u30', label: 'Dưới 30', max: 29 },
  { key: '30', label: '30–44', min: 30, max: 44 },
  { key: '45', label: '45–59', min: 45, max: 59 },
  { key: '60', label: '60+', min: 60 },
];
export function ageRange(key: string): { ageMin?: number; ageMax?: number } {
  const b = AGE_BUCKETS.find((x) => x.key === key);
  return b ? { ...(b.min != null ? { ageMin: b.min } : {}), ...(b.max != null ? { ageMax: b.max } : {}) } : {};
}

interface LookLike { id: string; lookKey: string; canonicalRefUrl?: string | null; stale?: boolean }

/**
 * Thứ tự gen lại các bộ "dựng từ mặt cũ" của MỘT nhân vật — bộ chính trước. Bộ chính cũ ⇒ gen lại nó rồi MỌI bộ phụ đang có ảnh (ảnh bộ
 * chính đổi thì bộ phụ nào cũng thành cũ). Bộ chính không cũ ⇒ chỉ bộ phụ đang cũ. `looks` đã xếp `default` đầu (`looksOf`).
 */
export function staleRegenPlan<T extends LookLike>(looks: T[]): T[] {
  const main = looks.find((l) => l.lookKey === 'default') || looks[0];
  if (!main) return [];
  const others = looks.filter((l) => l.id !== main.id && l.canonicalRefUrl);
  if (main.stale && main.canonicalRefUrl) return [main, ...others];
  return others.filter((l) => l.stale);
}

/** Câu xác nhận gen lại hàng loạt: số ảnh + số asset AVIS vĩnh viễn (mỗi ảnh mới tự đăng ký KYC — E3-P3). Không chữ giá. */
export function regenConfirmText(n: number): string {
  return `Sẽ gen ${n} ảnh, bộ chính trước. Mỗi ảnh mới tự đăng ký KYC trên AVIS ⇒ tạo thêm ${n} asset (không xoá được).`;
}

/**
 * 261002 — model của ô *Gen lại ▾* và ô Custom Prompt: LUÔN hiện đủ Grok · 3 model GPT image · Nano Banana (NB2 + Gemini 3 Pro Image) · Seedream 5.0 Pro (261014, chưa đo ⇒ nhãn "(chưa đo)") cho mọi loại look; thông tin đã đo chuyển thành
 * GỢI Ý trong nhãn thay vì ẩn. Chọn model lỗi/bị chặn ⇒ server tự lùi NB2 + cảnh báo. `kind` null (look không gen được) ⇒ rỗng.
 */
export const LOOK_MODEL_IDS = [
  'x-ai/grok-imagine-image-quality',
  'openai/gpt-image-2.5-flare',
  'openai/gpt-image-2.5-sunburst',
  'openai/gpt-5-image',
  'google/gemini-3.1-flash-image',
  'google/gemini-3-pro-image',
  'bytedance-seed/seedream-5-0-pro',
] as const;

/** Gợi ý đo đạc cho một model theo loại ảnh vào: OpenAI chặn ảnh người; chưa đo cho loại này; không gợi ý khi đã đo đạt. */
export function sheetModelHint(m: ImageModelInfo, kind: SheetInputKind): string {
  if (m.blocksHumanPhotoRef && kind !== 'text') return ' (OpenAI thường chặn ảnh người làm tham chiếu)';
  const ok = kind === 'text' ? !!m.sheetTextOk : kind === 'sheet' ? !!m.sheetFromSheetOk : !!m.sheetFromPortraitOk;
  return ok ? '' : ' (chưa đo)';
}

export function sheetModelOptions(models: ImageModelInfo[], kind: SheetInputKind | null | undefined): ImageModelInfo[] {
  if (!kind) return [];
  const out: ImageModelInfo[] = [];
  for (const id of LOOK_MODEL_IDS) {
    const m = models.find((x) => x.id === id && x.provider !== 'gemini');
    if (m) out.push({ ...m, label: `${m.label}${sheetModelHint(m, kind)}` });
  }
  return out;
}

/**
 * 261002 — ảnh tham chiếu mặt/bộ chính trong ô soạn nay GỠ ĐƯỢC (không khoá). Gỡ ⇒ request `noFaceRef: true` (server dựng TỪ CHỮ) và prompt điền sẵn phải là bản từ chữ.
 * `needsPromptRefetch`: có đổi trạng thái ref (gỡ) mà người dùng CHƯA sửa prompt (`prompt === origPrompt`) ⇒ nạp lại prompt tương ứng; đã sửa ⇒ giữ chữ của họ.
 */
export function needsPromptRefetch(a: { prompt: string; origPrompt: string }): boolean {
  return a.prompt === a.origPrompt;
}
/** Cờ gửi server: có ảnh tham chiếu mặt/bộ chính ban đầu mà đã bị gỡ khỏi hàng ref. */
export function noFaceRefFlag(refs: { key: string; isFaceRef?: boolean }[], hadFaceRef: boolean): boolean {
  return hadFaceRef && !refs.some((r) => r.isFaceRef);
}

/** 261002 (review F-01) — ô vuông thay ảnh khi bộ chính sẽ dựng MẶT FLARE MỚI (chưa có ảnh để hiện): vẫn có chip ✕ để gỡ ⇒ dựng từ chữ. */
export const FRESH_FACE_TILE =
  `data:image/svg+xml;utf8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64"><rect width="64" height="64" rx="6" fill="#eef4ff"/><text x="32" y="28" font-size="11" text-anchor="middle" fill="#1d4ed8" font-family="sans-serif">Mặt mới</text><text x="32" y="44" font-size="10" text-anchor="middle" fill="#1d4ed8" font-family="sans-serif">(Flare)</text></svg>')}`;
export const FRESH_FACE_LABEL = 'Mỗi lần Gen, Flare vẽ một mặt mới rồi dựng ảnh từ mặt đó — gỡ để dựng từ chữ';
export const FACE_REF_LABEL = 'Ảnh mặt / bộ chính — giữ mặt; gỡ để dựng từ chữ';

/** Ảnh tham chiếu mặt/bộ chính ban đầu của ô soạn từ phản hồi `sheet-prompt`: ảnh thật (`faceUrl`) → ô "Mặt mới (Flare)" (`freshFace`) → không có. */
export function initialFaceRef(p: { faceUrl?: string | null; freshFace?: boolean }, labelOverride?: string): { key: string; url: string; label: string; isFaceRef: true } | null {
  if (p.faceUrl) return { key: 'face-ref', url: p.faceUrl, label: labelOverride || FACE_REF_LABEL, isFaceRef: true };
  if (p.freshFace) return { key: 'fresh-face', url: FRESH_FACE_TILE, label: FRESH_FACE_LABEL, isFaceRef: true };
  return null;
}

/** Loại ảnh vào để gợi ý model: đã gỡ ref mặt/bộ chính ⇒ 'text'; còn ⇒ loại server báo (null giữ null). */
export function effectiveSheetKind(kind: SheetInputKind | null | undefined, faceRefRemoved: boolean): SheetInputKind | null {
  if (!kind) return null;
  return faceRefRemoved ? 'text' : kind;
}
